import React from "react";
import { ApiKeyOrGetter } from "../../../services/GeneralService";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import { 
  InfoIcon, 
  FileTextIcon,
  CheckCircleIcon
} from "lucide-react";
import TestSetAddedRequestsTable from "../TestSetAddedRequestsTable";

interface TestSetData {
  name?: string;
  description?: string;
  requests?: ChatCompletionRequest[];
  is_golden?: boolean;
  sourceType?: "manual" | "intent_group" | "dataset";
  sampleType?: "all" | "random";
  samplePercentage?: number;
  datasetId?: number;
}

interface TestSetReviewProps {
  testSetData: TestSetData;
  apiKey: ApiKeyOrGetter;
  onChange?: (data: Partial<TestSetData>) => void;
}

const TestSetReview: React.FC<TestSetReviewProps> = ({
  testSetData,
  apiKey,
  onChange = () => {},
}) => {
  const sourceType = testSetData.sourceType || "manual";
  const samplePercentage = testSetData.samplePercentage ?? 100;

  const sourceLabel =
    sourceType === "dataset"
      ? `Sample within a dataset${testSetData.datasetId ? ` (#${testSetData.datasetId})` : ""} (${samplePercentage}%)`
      : sourceType === "intent_group"
      ? `Sample across intent group (${samplePercentage}%)`
      : "Manual selection";

  const requestCountLabel =
    sourceType === "manual"
      ? `${testSetData.requests?.length || 0} requests`
      : sourceType === "dataset"
      ? samplePercentage >= 100
        ? "100% of dataset"
        : `~${samplePercentage}% of dataset`
      : samplePercentage >= 100
      ? "All eligible requests"
      : `~${samplePercentage}% sample`;

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div className="space-y-2">
        <h3 className="text-xl font-gc-med text-foreground">Review Test Set</h3>
        <p className="text-sm text-muted-foreground">
          Review your test set configuration before creating
        </p>
      </div>

      {/* Configuration Summary */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <FileTextIcon className="w-5 h-5 text-maitai-green" />
            Test Set Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Basic Details */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-sm font-gc-med text-foreground">Name</Label>
              <div className="text-base font-gc-reg text-foreground">
                {testSetData.name || "Not set"}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-gc-med text-foreground">Requests</Label>
              <div className="text-base font-gc-reg text-foreground">
                {requestCountLabel}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-sm font-gc-med text-foreground">Description</Label>
            <div className="text-base font-gc-reg text-foreground">
              {testSetData.description || "No description provided"}
            </div>
          </div>

          {/* Source */}
          <div className="space-y-2">
            <Label className="text-sm font-gc-med text-foreground">Source</Label>
            <div className="text-base font-gc-reg text-foreground">
              {sourceLabel}
            </div>
          </div>

          {/* Golden Setting */}
          <div className="flex items-center justify-between p-4 bg-card/50 border border-border rounded-lg">
            <div className="flex items-center gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-gc-med text-foreground">Golden Test Set</Label>
                  <InfoIcon className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground">
                  Test sets marked as 'Golden' will be used to evaluate the performance of models after fine-tuning
                </p>
              </div>
            </div>
            <Switch
              checked={testSetData.is_golden || false}
              onCheckedChange={(checked) => onChange({ is_golden: checked })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      {sourceType === "manual" && testSetData.requests && testSetData.requests.length > 0 && (
        <TestSetAddedRequestsTable
          requests={testSetData.requests}
          apiKey={apiKey}
          title="Selected Requests"
          showHeader={true}
        />
      )}

      {/* Summary Card */}
      <Card className="border-border bg-maitai-green/5">
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <CheckCircleIcon className="w-5 h-5 text-maitai-green" />
            <div>
              <h4 className="text-base font-gc-med text-foreground">Ready to Create</h4>
              <p className="text-sm text-muted-foreground">
                Your test set is configured and ready to be created ({sourceLabel}).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TestSetReview;
